import React, { useState, useRef, useEffect } from "react";
import { FaRegPenToSquare } from "react-icons/fa6";

interface InlineTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  displayClassName?: string;
  isSimple?: boolean;
  disabled?: boolean;
}

const InlineTextArea: React.FC<InlineTextAreaProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  placeholder = "Click to edit",
  className = "",
  displayClassName = "",
  isSimple = false,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Simple mode - just a plain input, no editing state
  if (isSimple) {
    return (
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-info flex-1 ${className}`}
      />
    );
  }

  const handleSave = () => {
    onSave?.();
    setIsEditing(false);
  };

  const handleCancel = () => {
    onCancel?.();
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleBlur = () => {
    // Small delay to allow button clicks to register
    setTimeout(() => {
      if (isEditing) {
        handleCancel();
      }
    }, 150);
  };

  if (disabled || !isEditing) {
    return (
      <span
        onClick={() => setIsEditing(true)}
        className={`text-balance flex flex-row gap-2 justify-between pr-2 items-center p-1 m-1 rounded transition-colors ${!disabled ? "hover:bg-secondary-light cursor-pointer" : "cursor-not-allowed"} ${displayClassName}`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {value ? (
          <span>{value}</span>
        ) : (
          <span className="text-gray-400 italic">{placeholder}</span>
        )}
        <div>
          <FaRegPenToSquare
            className={`opacity-70 ${isHovering ? "" : "collapse"}`}
          />
        </div>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`p-1 m-1 rounded border border-gray-300 min-h-[8vh] focus:outline-none focus:ring-2 focus:ring-info flex-1 ${className}`}
      />
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="p-2 rounded bg-success text-white hover:bg-success-dark transition-colors"
          aria-label="Save"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleCancel();
          }}
          className="p-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors"
          aria-label="Cancel"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export { InlineTextArea };
